import React, { Component } from 'react';

import { RelatedEntityGrid as EntityGrid } from 'hay-core-react';
import { ModelDropDown } from 'hay-core-react';
import { ModelTextInput } from 'hay-core-react';
import { ModelCalendar } from 'hay-core-react';
import { Usuario } from 'hay-core-react';
import { decodificarContextoRelacao } from 'hay-core-react';
import { UtilService } from 'hay-core-react';

import UsuarioEvento               from '../../../models/UsuarioEvento';
import UsuarioEventoTipoEventoEnum from '../../../enums/UsuarioEventoTipoEventoEnum';
import UsuarioEventoTimeline       from './UsuarioEventoTimeline';

const TAMANHO_PAGINA = 100;

// ─── Esquema de campos ────────────────────────────────────────────────────────

const model = {
    usuario: {
        exibir:       'main.usuarioevento.usuario',
        visivel:      true,
        tipo:         'relacao',
        camporelacao: 'nome',
        entidade:     'usuario.entidade',
        template:     'usuario.template',
    },
    tipoevento: {
        exibir:   'main.usuarioevento.tipoevento',
        visivel:  true,
        tipo:     'enum',
        entidade: 'usuarioevento.tipoevento',
    },
    descricao: { exibir: 'main.usuarioevento.descricao', visivel: true },
    createdAt: { exibir: 'main.createdAt', visivel: true, tipo: 'date', time: true },
    // Referencia polimorfica pro registro de origem - so pra suporte/depuracao, sem coluna na
    // grid por padrao (mesmo tratamento de UsuarioTransacao.ultimoretorno).
    origemmodel: { exibir: 'main.usuarioevento.origemmodel', hidegrid: true },
    origemid: { exibir: 'main.usuarioevento.origemid', hidegrid: true },
};

// ─── Renderers customizados ───────────────────────────────────────────────────

const renderers = {
    usuario: { entidade: Usuario, template: (rowData) => rowData.usuario?.nome || rowData.usuario?.id || '' },
    'usuarioevento.tipoevento': UsuarioEventoTipoEventoEnum,
};

// ─── Formulário (só "Ver" - entidade de histórico, gerada pelo sistema) ───────

class UsuarioEventoFormulario extends Component {
    render() {
        const { model, contexto } = this.props;
        return (
            <div className="grid">
                <div className="field col-12 md:col-6 mt-3">
                    <ModelDropDown model={model.usuario} contexto={contexto} />
                </div>
                <div className="field col-12 md:col-6 mt-3">
                    <ModelDropDown model={model.tipoevento} contexto={contexto} />
                </div>
                <div className="field col-12 mt-3">
                    <ModelTextInput model={model.descricao} contexto={contexto} />
                </div>
                <div className="field col-12 md:col-6 mt-3">
                    <ModelCalendar model={{ ...model.createdAt, disabled: true }} contexto={contexto} />
                </div>
                <div className="field col-12 md:col-6 mt-3">
                    <ModelTextInput model={model.origemmodel} contexto={contexto} />
                </div>
                <div className="field col-12 md:col-6 mt-3">
                    <ModelTextInput model={model.origemid} contexto={contexto} />
                </div>
            </div>
        );
    }
}

// ─── Template (componente exportado) ──────────────────────────────────────────

// Duas visualizações, alternadas pelo seletor padrão do EntityGrid (o mesmo usado em
// GrupoPermissao - templateSimples/onAtualizarSimples/modoVisualizacaoInicial): grade (padrão,
// somente leitura) e linha do tempo (modo "simples", exibida por padrão nesta tela - eventos são,
// por natureza, uma sucessão cronológica, então a timeline é a leitura mais natural). A grade
// continua disponível para quem precisar filtrar/ordenar/exportar.
//
// A timeline busca seus próprios dados (UsuarioEvento.list, filtrado por usuario=parentId) porque
// templateSimples não recebe o filtroInicial calculado internamente pelo RelatedEntityGrid — só a
// grade (modo padrão) usa esse filtro automaticamente.
class UsuarioEventoTemplate extends Component {
    constructor(props) {
        super(props);
        this.state = {
            eventos: [],
            carregando: false,
            temMais: false,
            limite: TAMANHO_PAGINA,
        };
        this.carregarTimeline = this.carregarTimeline.bind(this);
        this.carregarMais = this.carregarMais.bind(this);
    }

    componentDidUpdate(prevProps) {
        const { parentId } = decodificarContextoRelacao(this.props.searchparams);
        const { parentId: parentIdAnterior } = decodificarContextoRelacao(prevProps.searchparams);
        if (parentId !== parentIdAnterior) {
            this.setState({ eventos: [], limite: TAMANHO_PAGINA }, this.carregarTimeline);
        }
    }

    async carregarTimeline() {
        const { parentId } = decodificarContextoRelacao(this.props.searchparams);
        this.setState({ carregando: true });

        const filtro = parentId
            ? { usuario: { constraints: [{ value: parentId, matchMode: 'equals' }] } }
            : {};
        const resultado = await UsuarioEvento.list(
            filtro,
            { sortField: 'createdAt', sortOrder: -1 },
            { rows: this.state.limite },
        );

        // EntidadeBase.list()/ApiService.get() sempre embrulham a resposta como
        // { data: { count, registros }, status } - nunca { registros, count } direto na raiz
        // (mesmo formato que EntityGrid.atualizar() consome pra grade padrão).
        if (!resultado?.status) {
            UtilService.showToast({ sticky: true, severity: 'error', summary: UtilService.traduzir('main.erroCarregar'), detail: UtilService.traduzir('main.erroCarregar.detalhe') });
            this.setState({ carregando: false });
            return;
        }

        const eventos = resultado.data?.registros || [];
        this.setState({
            eventos,
            carregando: false,
            temMais: (resultado.data?.count || 0) > eventos.length,
        });
    }

    carregarMais() {
        this.setState({ limite: this.state.limite + TAMANHO_PAGINA }, this.carregarTimeline);
    }

    renderTimeline(header) {
        return (
            <div>
                {header}
                <UsuarioEventoTimeline
                    itens={this.state.eventos}
                    carregando={this.state.carregando}
                    temMais={this.state.temMais}
                    onCarregarMais={this.carregarMais}
                />
            </div>
        );
    }

    render() {
        return (
            <EntityGrid
                entidade={UsuarioEvento}
                model={model}
                identidade="main.usuarioevento"
                permissoes={this.props.permissoes}
                icone="pi-bell"
                storageKey="dt-usuarioeventos"
                relacionados={[]}
                renderers={renderers}
                configuracao={this.props.configuracao}
                navigate={this.props.navigate}
                location={this.props.location}
                searchparams={this.props.searchparams}
                ordenacaoPadrao={{ sortField: 'createdAt', sortOrder: -1 }}
                formulario={(m, ctx) => <UsuarioEventoFormulario model={m} contexto={ctx} />}
                templateSimples={(header) => this.renderTimeline(header)}
                onAtualizarSimples={this.carregarTimeline}
                modoVisualizacaoInicial="simples"
            />
        );
    }
}

export default UsuarioEventoTemplate;
